import { useState, useContext } from 'react';

import endpoints from '../../../../../../api/endpoints';
import { useDialog } from '../../../../../common/dialog/useDialog';
import { BusinessError } from '../../../../../common/business-error';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { ToastNotificationContext } from '../../../../../common/toast-notification';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import { UssdAppFormData } from './interfaces';
import { getCreateAppBusinessError } from './businessErrors';

export type DialogError = BusinessError | null;

export const useCreateApp = () => {
  const [isCreateAppLoading, setCreateAppLoading] = useState<boolean>(false);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [error, setError] = useState<DialogError>(null);
  const { openToast } = useContext(ToastNotificationContext);

  const handleOpenCreateAppDialog = () => {
    setError(null);
    handleOpenDialog();
  };

  const handleCloseCreateAppDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  const handleSubmitCreateAppDialog = async (ussdAppCreateFormData: UssdAppFormData, onSuccess: () => void) => {
    if (ussdAppCreateFormData) {
      setCreateAppLoading(true);
      setError(null);

      try {
        const response = await fetchWithTokenRefresh(endpoints.ussdApp, addParams('POST', ussdAppCreateFormData));

        if (response.status !== 200) {
          await handleResponseError(response);
        }

        onSuccess();
        setCreateAppLoading(false);
        handleCloseCreateAppDialog();
        openToast({
          message: `${ussdAppCreateFormData.appId} app created successfully`,
          severity: 'success'
        });
      } catch (e) {
        const { message, severity } = getCreateAppBusinessError(e);

        setError({ message, severity });
        setCreateAppLoading(false);
      }
    }
  };

  return {
    error,
    isCreateAppDialogOpen: isDialogOpen,
    isCreateAppLoading,
    handleOpenCreateAppDialog,
    handleCloseCreateAppDialog,
    handleSubmitCreateAppDialog
  };
};
